import { useState, useCallback, useEffect, useMemo } from 'react';
// import _ from 'lodash';
import {
  FormControl,
  Grid,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  InputLabel,
  InputAdornment,
  IconButton,
  useTheme,
} from '@mui/material';
import Button from '@mui/material/Button';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import {
  FieldParam,
  DropdownFieldValue,
  SelectWithSingleInputParam,
  FieldParamValue,
  ValidateState,
  ControlsType,
  OptionLegParam,
} from '../types';
import { cloneDeep } from 'lodash';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import { generateValuesBeforeSend } from '../Utils/utils';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CustomTooltip from 'app/design/uiComponents/CustomTooltip';

type Param = Omit<FieldParam, 'renderer' | 'type'>;
interface Props {
  param: Param;
  selected: Record<string, FieldParamValue>;
  onChange: (key: string, value: Record<string, FieldParamValue>) => void;
  sendValidation: (key: string, isValid: boolean) => void;
  isReadOnly: boolean;
  optionLeg?: OptionLegParam;
}

export const SelectWithSingleInputRenderer = (props: Props) => {
  const { param, onChange, sendValidation, isReadOnly } = props;
  const [showAddCheckbox, setShowAddCheckbox] = useState<boolean>(
    props.selected.toggleStatus as boolean,
  );
  let selected = props.selected;
  const {
    name,
    controls,
    parentKey,
    myStyle,
    addClass,
    width,
    isAddButtonActive,
  } = param as SelectWithSingleInputParam;
  const [isValidStatus, setIsValidStatus] = useState<ValidateState>({
    error: '',
    status: false,
  });

  const [userInput, setUserInput] = useState(selected);
  const globaltheme = useTheme();

  const validate = useCallback(
    (formData: Record<string, FieldParamValue>) => {
      if (Object.prototype.hasOwnProperty.call(formData, 'toggleStatus')) {
        delete formData.toggleStatus;
      }
      let validStatus = {
        status: false,
        error: '',
      };
      if (!selected.toggleStatus) {
        Object.keys(formData).forEach((item, index) => {
          if (
            !Object.values(formData).includes('none') &&
            (!formData[item] || (formData[item] && formData[item] === '0')) &&
            controls[index]?.isRequired
          ) {
            validStatus = {
              status: true,
              error: `Please enter valid ${
                controls[index].type === 'dropdown' ? 'dropdown' : 'input'
              } data`,
            };
          }
        });
      }
      return validStatus;
    },
    [controls, selected],
  );

  useEffect(() => {
    const validStatus = validate(cloneDeep(selected));
    setIsValidStatus(validStatus);
    sendValidation(parentKey, !validStatus.status);

    setShowAddCheckbox(
      isReadOnly ? !isReadOnly : (props.selected.toggleStatus as boolean),
    );
  }, [
    validate,
    parentKey,
    sendValidation,
    selected,
    props.selected.toggleStatus,
    isReadOnly,
  ]);

  const theme = useMemo(() => {
    return createTheme({
      components: {
        MuiSelect: {
          styleOverrides: {
            icon: {
              color: globaltheme.palette.text.secondary,
            },
          },
        },
        MuiSvgIcon: {
          styleOverrides: {
            root: {
              display: `${isReadOnly ? 'none' : 'block'}`,
              color: globaltheme.palette.text.secondary,
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            input: {
              color: globaltheme.palette.text.primary,
              '&.Mui-disabled': {
                color: globaltheme.palette.text.disabled,
                ' -webkit-text-fill-color': globaltheme.palette.text.disabled,
              },
            },
            notchedOutline: {
              border: 'none',
              paddingRight: 0,
            },
            root: {},
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              top: '7px',
            },
            shrink: {
              top: '0px',
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundColor: globaltheme.palette.common.white,
              color: globaltheme.palette.common.black,
            },
          },
        },
      },
    });
  }, [isReadOnly, globaltheme]);

  const onChangeFieldType = (
    event: SelectChangeEvent<string>,
    control: ControlsType,
  ) => {
    if (event.target.value === 'Remove') {
      setShowAddCheckbox(!showAddCheckbox);
      sendValidation(parentKey, true);
      Object.keys(selected).forEach(item => {
        const currentItem = controls.find(e => e.key === item);
        if (item === control.key) {
          selected[item] = control.defaultValue as string;
        } else {
          selected[item] = currentItem?.defaultValue
            ? currentItem.defaultValue
            : selected[item];
        }
      });
      selected.toggleStatus = true;
      onChange(parentKey, selected);
    } else {
      selected[control.key] = event.target.value;
      const validStatus = validate(cloneDeep(selected));
      sendValidation(parentKey, !validStatus.status);
      onChange(parentKey, selected);
    }
  };

  const onInputFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    control: ControlsType,
  ) => {
    selected[control.key] = event.target.value;
    const validStatus = validate(cloneDeep(selected));
    setIsValidStatus(validStatus);
    sendValidation(parentKey, !validStatus.status);
    setUserInput(selected);
  };

  const onFocusOutHandler = () => {
    const validStatus = validate(cloneDeep(userInput));
    sendValidation(parentKey, !validStatus.status);
    onChange(parentKey, selected);
  };
  return (
    <>
      {isReadOnly && controls.some(e => selected[e.key] === 'none') ? null : (
        <Grid
          item
          className={addClass}
          style={
            width
              ? {
                  marginBottom: '24px',
                  alignSelf: 'center',
                  width: `${width}px`,
                }
              : { marginBottom: '24px', alignSelf: 'center' }
          }
        >
          <GlobalStyles
            styles={{
              [theme.breakpoints.down('md')]: myStyle?.small
                ? myStyle.small
                : myStyle?.root
                ? myStyle.root
                : myStyle,
              [theme.breakpoints.up('md')]: myStyle?.medium
                ? myStyle.medium
                : myStyle?.root
                ? myStyle.root
                : myStyle,
              [theme.breakpoints.up('lg')]: myStyle?.large
                ? myStyle.large
                : myStyle?.root
                ? myStyle.root
                : myStyle,
            }}
          />
          {(showAddCheckbox && isAddButtonActive) ||
          controls.some(e => selected[e.key] === 'none') ? (
            <CustomTooltip tooltipKey={param.uniqueKey}>
              {() => (
                <Button
                  variant="outlined"
                  startIcon={<AddCircleIcon />}
                  onClick={() => {
                    const checkboxField = controls.find(
                      e => e.type === 'checkbox',
                    );
                    if (
                      checkboxField &&
                      Object.prototype.hasOwnProperty.call(
                        selected,
                        checkboxField.key,
                      )
                    ) {
                      selected[checkboxField.key] =
                        !selected[checkboxField.key];
                    }
                    selected.toggleStatus = false;
                    setShowAddCheckbox(!showAddCheckbox);
                    const validStatus = validate(cloneDeep(userInput));
                    sendValidation(parentKey, !validStatus.status);
                    selected = generateValuesBeforeSend(param, selected);
                    onChange(parentKey, selected);
                  }}
                  className="remove-border"
                >
                  {name}
                </Button>
              )}
            </CustomTooltip>
          ) : (
            <ThemeProvider theme={theme}>
              <FormControl
                variant="outlined"
                className={`select-with-single-input-box ${
                  isValidStatus.status ? 'error' : ''
                } ${width ? 'full-width' : width}`}
              >
                {controls.map(control => (
                  <>
                    <InputLabel
                      id={control.key}
                      sx={{
                        backgroundColor:
                          globaltheme.component.mainLayoutContainer.main,
                        padding: '0 7px',
                        color: globaltheme.palette.text.secondary,
                      }}
                    >
                      {name}
                    </InputLabel>
                    {control.type === 'dropdown' ? (
                      <Select
                        onChange={(event: SelectChangeEvent<string>) => {
                          onChangeFieldType(event, control);
                        }}
                        name={control.key}
                        id={name}
                        className="select"
                        value={selected[control.key] as string}
                        style={
                          width
                            ? {
                                width: `${width}px`,
                              }
                            : {}
                        }
                        disabled={isReadOnly || !control.enabled}
                        error={isValidStatus.status}
                      >
                        {control.values?.map((value: DropdownFieldValue) => (
                          <MenuItem
                            key={value.key}
                            color="red"
                            value={value.key}
                            className="select-option"
                          >
                            {value.label}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : control.type === 'input' ? (
                      <TextField
                        type="number"
                        variant="outlined"
                        sx={{
                          width: `${width} !important`,
                          '& .css-1uu6ptx-MuiInputBase-root-MuiOutlinedInput-root':
                            {
                              paddingRight: 0,
                            },
                        }}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => onInputFieldChange(event, control)}
                        name={control.key}
                        onBlur={onFocusOutHandler}
                        value={selected[control.key] as string}
                        error={isValidStatus.status}
                        disabled={isReadOnly || !control.enabled}
                        className="text-field"
                        InputProps={{
                          endAdornment: control.tooltip && (
                            <InputAdornment position="end">
                              <CustomTooltip tooltipKey={control.tooltip}>
                                {({ isTooltipAvailable }) =>
                                  isTooltipAvailable ? (
                                    <IconButton>
                                      <InfoOutlinedIcon />
                                    </IconButton>
                                  ) : (
                                    <></>
                                  )
                                }
                              </CustomTooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                    ) : null}
                  </>
                ))}
              </FormControl>
            </ThemeProvider>
          )}
        </Grid>
      )}
    </>
  );
};
